import { useEffect, useState } from 'react';
import { requestPasswordReset, confirmPasswordReset } from '../../utils/api';

interface Props {
  open: boolean;
  initialToken?: string | null;
  onClose: () => void;
}

type Stage = 'request' | 'sent' | 'reset' | 'done' | 'error';

/** Two-phase password recovery:
 *   1. User enters email → POST /auth/password-reset/request → "Check inbox"
 *   2. User clicks email link (carries ?reset_token=…) → app re-opens this
 *      modal in stage='reset' where they pick a new password.
 *
 * The two stages live in one modal so the user has a single mental model
 * for "I'm in recovery mode". */
export default function ForgotPasswordModal({ open, initialToken, onClose }: Props) {
  const [stage, setStage] = useState<Stage>('request');
  const [email, setEmail] = useState('');
  const [newPw, setNewPw] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // If a reset token is present in the URL, jump straight to the new-password
  // form. The token field is read-only — the user just confirms by setting a
  // password.
  useEffect(() => {
    if (open && initialToken) setStage('reset');
  }, [open, initialToken]);

  if (!open) return null;

  const handleRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      await requestPasswordReset(email.trim());
      setStage('sent');
    } catch (e) {
      // Generic ack: even on network errors we tell the user "check inbox"
      // so the endpoint stays non-enumerable. Real network failures show
      // up in the API client's retry logic / Sentry.
      setStage('sent');
    } finally {
      setBusy(false);
    }
  };

  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!initialToken) {
      setErr('חסר קוד איפוס. פתחי שוב את הקישור מהמייל.');
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await confirmPasswordReset(initialToken, newPw);
      setStage('done');
      // strip ?reset_token from the URL so refresh doesn't re-enter the modal
      try {
        const url = new URL(window.location.href);
        url.searchParams.delete('reset_token');
        window.history.replaceState(null, '', url.toString());
      } catch {/* ignore */}
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center px-4"
      dir="rtl"
      onClick={onClose}
    >
      <div className="bg-slate-900 border border-violet-500/30 rounded-xl p-6 max-w-sm w-full" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-bold text-lg mb-3 text-slate-100">איפוס סיסמה</h3>

        {stage === 'request' && (
          <form onSubmit={handleRequest} className="space-y-3">
            <p className="text-sm text-slate-400">
              הזיני את כתובת המייל שאיתה נרשמת. נשלח לך קישור לאיפוס.
            </p>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="email@example.com"
              required
              dir="ltr"
              className="w-full p-2.5 rounded-lg text-sm bg-slate-800 border border-slate-700 focus:border-violet-500 outline-none"
            />
            <button
              type="submit"
              disabled={busy || !email.trim()}
              className="w-full bg-violet-600 hover:bg-violet-500 disabled:opacity-60 text-white py-2 rounded-lg font-bold"
            >
              {busy ? 'שולח…' : 'שלחי קישור איפוס'}
            </button>
          </form>
        )}

        {stage === 'sent' && (
          <div className="space-y-3 text-sm">
            <p className="text-slate-300">
              שלחנו לך קישור איפוס לתיבה <strong dir="ltr">{email}</strong> (אם החשבון קיים). פתחי את ההודעה ולחצי על
              "איפוס סיסמה". הקישור תקף שעה.
            </p>
            <p className="text-slate-500 text-xs">לא רואה? בדקי גם בתיבת ספאם.</p>
            <button onClick={onClose} className="w-full bg-slate-700 hover:bg-slate-600 text-white py-2 rounded-lg">
              סגרי
            </button>
          </div>
        )}

        {stage === 'reset' && (
          <form onSubmit={handleReset} className="space-y-3">
            <p className="text-sm text-slate-400">בחרי סיסמה חדשה (לפחות 8 תווים, אותיות ומספרים).</p>
            <input
              type="password"
              value={newPw}
              onChange={(e) => setNewPw(e.target.value)}
              placeholder="סיסמה חדשה"
              required
              minLength={8}
              className="w-full p-2.5 rounded-lg text-sm bg-slate-800 border border-slate-700 focus:border-violet-500 outline-none"
            />
            <button
              type="submit"
              disabled={busy || newPw.length < 8}
              className="w-full bg-violet-600 hover:bg-violet-500 disabled:opacity-60 text-white py-2 rounded-lg font-bold"
            >
              {busy ? 'שומר…' : 'שמרי סיסמה'}
            </button>
            {err && <div className="text-rose-400 text-xs bg-rose-900/20 rounded px-2 py-1.5">{err}</div>}
          </form>
        )}

        {stage === 'done' && (
          <div className="space-y-3 text-sm">
            <p className="text-emerald-300">✓ הסיסמה הוחלפה. אפשר להתחבר עם החדשה.</p>
            <button onClick={onClose} className="w-full bg-violet-600 hover:bg-violet-500 text-white py-2 rounded-lg font-bold">
              חזרה למסך התחברות
            </button>
          </div>
        )}

        {stage !== 'done' && (
          <button onClick={onClose} className="block mt-4 text-xs text-slate-500 hover:text-slate-300 mx-auto">
            ביטול
          </button>
        )}
      </div>
    </div>
  );
}
